/**
 * Centralna definicja zdarzeń analitycznych (trackEvent).
 *
 * Cel: jeden, spójny kształt payloadu dla każdego eventu — żeby admin/analytics
 * widział te same pola niezależnie od miejsca w UI. Zmiana nazwy lub typu pola
 * wymaga aktualizacji TYLKO tutaj — TypeScript wskaże resztę call-sites.
 *
 * KONWENCJE PÓL (używaj zawsze tych samych nazw i typów):
 *   - shelter_id:   string (UUID schroniska)
 *   - shelter_name: string (oryginalna nazwa)
 *   - shelter_city: string
 *   - slug:         string | undefined (slug schroniska, jeśli mamy)
 *   - source:       "map" | "list" | "search" | "shelter_page" | "modal" | "unknown"
 *                   (skąd użytkownik trafił do akcji)
 *   - type:         "needs" | "info" | "full" | "nowe"
 *                   (typ formularza zgłoszeniowego — tylko dla eventów /zglos)
 *   - type_raw:     string (surowa wartość ?type= przed normalizacją — debug)
 */

import type { Shelter } from "@/types/shelter";

/** Spójna lista źródeł akcji. */
export type EventSource =
  | "map"
  | "list"
  | "search"
  | "shelter_page"
  | "modal"
  | "unknown";

/** Typ aktualizacji dopuszczalny w trackingu (PL etykieta z helpera). */
export type EventUpdateType = "potrzeby" | "dane" | "pełny" | "nowe";

/** Wspólny zestaw pól identyfikujących schronisko. */
export interface ShelterEventFields {
  shelter_id: string;
  shelter_name: string;
  shelter_city: string;
  slug?: string;
}

/** Wyciąga ujednolicony zestaw pól z obiektu Shelter. */
export const shelterFields = (
  shelter: Pick<Shelter, "id" | "name" | "city"> & { slug?: string | null },
): ShelterEventFields => ({
  shelter_id: shelter.id,
  shelter_name: shelter.name,
  shelter_city: shelter.city,
  slug: shelter.slug ?? undefined,
});

/** Pola dla CTA „Zaktualizuj potrzeby/dane" — spójne między listą, mapą i stroną. */
export interface UpdateLinkEventFields extends ShelterEventFields {
  type: "needs" | "info";
  source: EventSource;
}

export const updateLinkFields = (
  shelter: Pick<Shelter, "id" | "name" | "city"> & { slug?: string | null },
  type: "needs" | "info",
  source: EventSource,
): UpdateLinkEventFields => ({
  ...shelterFields(shelter),
  type,
  source,
});

/**
 * Mapa: nazwa eventu → kształt payloadu.
 * Dodając nowy event, dopisz go tutaj — TypeScript wymusi zgodność wywołań.
 */
export interface EventPayloads {
  // Otwarcie karty schroniska — z różnych źródeł
  "Otworzono schronisko": ShelterEventFields & { source: EventSource };

  // CTA aktualizacji
  "Zaktualizuj potrzeby": UpdateLinkEventFields;
  "Zaktualizuj dane": UpdateLinkEventFields;

  // Nawigacja na pełną stronę schroniska
  "Otworz pelna strone": ShelterEventFields & { source: EventSource };

  // Akcje kontaktowe
  Telefon: ShelterEventFields;
  Email: ShelterEventFields;
  "Adres - mapa": ShelterEventFields;
  "Strona www": ShelterEventFields;

  // Udostępnianie
  "Udostępnij - otwarto": ShelterEventFields;
  "Udostępnij - skopiowano link": ShelterEventFields;
  "Udostępnij potrzeby": ShelterEventFields;
  "Udostępnij profil": ShelterEventFields;

  // Wejście z udostępnionego linku (utm_source=share)
  "Profil - wejście z linku": ShelterEventFields;

  // Formularz /zglos
  "Formularz otwarty": {
    type: EventUpdateType;
    type_raw: string;
    shelter_id?: string;
    slug?: string;
    prefilled?: boolean;
  };
  "Formularz wysłany": {
    type: EventUpdateType;
    type_raw: string;
    shelter_id?: string;
    slug?: string;
  };

  // Pozostałe (luźne payloady — nie wymagają shelter)
  Filtr: { filter: string; enabled: boolean };
  "Filtr miasto": { city: string };
  "Wyszukiwanie - wybrano": ShelterEventFields;
  "Wyszukiwanie - wybrano województwo": { region: string };
  "Wyszukiwanie bez wyników": { query: string };
  "Sekcja widoczna": { section: string };
  "FAQ - otwarto": Record<string, never>;
}

export type KnownEventName = keyof EventPayloads;
